/**
 * Phase 2.5.2 — Customer Review Interface ("Review Your Roles")
 *
 * Two-pane layout:
 *  - Left: workspace picker + uploaded source-document list with normalization status,
 *          plus a list of canonical JDs filtered by status.
 *  - Right: editable canonical-schema viewer for the selected canonical JD,
 *          with per-field source-attribution toggle, diff badges (AI vs human),
 *          status pill, confidence pill, approve/reject actions, and a
 *          bulk-approve toolbar that runs the stratified audit-sample workflow.
 *
 * Strict Realloc design tokens — no gradients; Playfair / Inter / JetBrains Mono.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
    UploadCloud, FileText, ShieldCheck, AlertCircle, CheckCircle2, XCircle,
    ChevronRight, Eye, Sparkles, Loader2, Search, RefreshCw, ArrowRight,
} from "lucide-react";
import { BlockSection } from "./jd_normalizer/JsonEditor";
import ConsistencyPanel from "./jd_normalizer/ConsistencyPanel";

// ---------- helpers --------------------------------------------------------
const cls = (...xs) => xs.filter(Boolean).join(" ");
const fmtPct = (v) => (v == null ? "—" : `${Math.round(v * 10) / 10}%`);

const STATUS_STYLES = {
    pending_review: { label: "Pending review", fg: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30" },
    approved:       { label: "Approved",       fg: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30" },
    rejected:       { label: "Rejected",       fg: "text-red-300", bg: "bg-red-500/10 border-red-500/30" },
    locked:         { label: "Locked to cycle", fg: "text-blue-300", bg: "bg-blue-500/10 border-blue-500/30" },
    draft:          { label: "Draft", fg: "text-gray-300", bg: "bg-white/[0.04] border-white/10" },
};

function StatusBadge({ status }) {
    const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
    return (
        <span data-testid={`status-badge-${status}`}
              className={cls("inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border", s.bg, s.fg)}>
            {s.label}
        </span>
    );
}

function ConfidencePill({ value }) {
    if (value == null) return null;
    const v = Math.round(value * 10) / 10;
    const tone =
        v >= 90 ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
        : v >= 75 ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
        : "text-red-300 border-red-500/40 bg-red-500/10";
    return (
        <span data-testid="confidence-pill" className={cls("inline-flex items-center font-mono text-[10px] px-1.5 py-0.5 border", tone)}>
            {v}% conf
        </span>
    );
}

// ---------- main page ------------------------------------------------------
export default function AdminJDNormalizerPage() {
    const { api } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [enterpriseId, setEnterpriseId] = useState("");
    const [sources, setSources] = useState([]);
    const [canonicals, setCanonicals] = useState([]);
    const [selectedCJID, setSelectedCJID] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [bulkOpen, setBulkOpen] = useState(false);
    const [pollKey, setPollKey] = useState(0);
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadMsg, setUploadMsg] = useState("");

    // Load workspaces
    useEffect(() => {
        api.get("/v2/admin/workspaces")
            .then(r => {
                const list = r.data?.workspaces || [];
                setWorkspaces(list);
                if (list.length && !enterpriseId) setEnterpriseId(list[0].id);
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    // Load sources + canonicals when workspace changes
    const refresh = useCallback(() => {
        if (!enterpriseId) return;
        Promise.all([
            api.get(`/v2/admin/jd-normalizer/sources/${enterpriseId}`),
            api.get(`/v2/admin/jd-normalizer/canonical-jds/${enterpriseId}`),
        ]).then(([s, c]) => {
            setSources(s.data?.sources || []);
            setCanonicals(c.data?.canonical_jds || []);
        }).catch(() => {});
    }, [api, enterpriseId]);

    useEffect(() => { refresh(); }, [refresh, pollKey]);

    // Auto-poll while a normalization is in flight (any source without canonical link)
    useEffect(() => {
        const inFlight = sources.some(s => !s.canonical && !s.extraction_warnings?.length && (s.extraction_confidence || 0) >= 60);
        if (!inFlight) return;
        const id = setInterval(() => setPollKey(k => k + 1), 5000);
        return () => clearInterval(id);
    }, [sources]);

    const filteredCJ = useMemo(() => {
        let list = canonicals;
        if (statusFilter !== "all") list = list.filter(c => c.approval_status === statusFilter);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            list = list.filter(c => (c.role_title || "").toLowerCase().includes(q) || (c.cadre_signal || "").toLowerCase().includes(q));
        }
        return list;
    }, [canonicals, statusFilter, search]);

    const handleUpload = async (file) => {
        if (!file || !enterpriseId) return;
        setUploading(true); setUploadMsg("");
        const fd = new FormData();
        fd.append("enterprise_id", enterpriseId);
        fd.append("file", file);
        try {
            const r = await api.post("/v2/admin/jd-normalizer/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setUploadMsg(`Uploaded "${r.data.filename}" (${r.data.detected_format}, ${r.data.extraction_confidence}% conf)`);
            // auto-trigger normalize if not blocked
            if (!r.data.blocked) {
                await api.post(`/v2/admin/jd-normalizer/normalize/${r.data.id}`);
                setUploadMsg(prev => prev + " — normalization queued.");
            }
            refresh();
        } catch (e) {
            setUploadMsg(`Upload failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const counts = useMemo(() => {
        const c = { pending_review: 0, approved: 0, rejected: 0, locked: 0 };
        canonicals.forEach(x => { c[x.approval_status] = (c[x.approval_status] || 0) + 1; });
        return c;
    }, [canonicals]);

    return (
        <div className="p-8 max-w-[1400px] space-y-6" data-testid="admin-jd-normalizer-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-display font-bold text-white">Review Your Roles</h1>
                    <p className="text-sm text-gray-500 mt-1 font-body">
                        Verify, edit, and approve AI-normalized job descriptions before locking them to a cycle.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={enterpriseId}
                        onChange={e => { setEnterpriseId(e.target.value); setSelectedCJID(null); }}
                        data-testid="workspace-picker"
                        className="bg-card border border-border/40 text-sm text-white px-3 py-2 focus:outline-none focus:border-white/30">
                        {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                    <button
                        onClick={refresh}
                        data-testid="refresh-btn"
                        className="px-2.5 py-2 border border-border/40 text-gray-400 hover:text-white hover:border-white/30 transition-colors">
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Counts strip */}
            <div className="grid grid-cols-4 gap-3">
                {[
                    { k: "pending_review", lbl: "Pending review", icon: AlertCircle },
                    { k: "approved", lbl: "Approved", icon: CheckCircle2 },
                    { k: "rejected", lbl: "Rejected", icon: XCircle },
                    { k: "locked", lbl: "Locked", icon: ShieldCheck },
                ].map(({ k, lbl, icon: Icon }) => (
                    <button key={k}
                        onClick={() => setStatusFilter(prev => prev === k ? "all" : k)}
                        data-testid={`count-card-${k}`}
                        className={cls(
                            "p-4 border bg-card text-left transition-colors",
                            statusFilter === k ? "border-white/40" : "border-border/40 hover:border-white/20"
                        )}>
                        <div className="flex items-center justify-between text-gray-500">
                            <Icon className="w-4 h-4" />
                            <span className="font-mono text-xs uppercase tracking-wider">{lbl}</span>
                        </div>
                        <p className="text-3xl font-display font-bold text-white mt-1">{counts[k] || 0}</p>
                    </button>
                ))}
            </div>

            {/* Two-pane layout */}
            <div className="grid grid-cols-12 gap-4 min-h-[600px]">
                {/* LEFT pane */}
                <div className="col-span-4 space-y-4">
                    {/* Upload */}
                    <div className="border border-border/40 bg-card p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-mono">Upload JD</p>
                        <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt,.md,.html,.htm" onChange={e => handleUpload(e.target.files?.[0])}
                               className="hidden" data-testid="file-input" />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={!enterpriseId || uploading}
                            data-testid="upload-btn"
                            className="w-full flex items-center justify-center gap-2 py-3 text-sm border border-white/20 text-white hover:bg-white/[0.04] transition-colors disabled:opacity-50">
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                            <span>{uploading ? "Uploading…" : "Choose file (PDF, DOCX, TXT, HTML)"}</span>
                        </button>
                        {uploadMsg && <p className="text-[11px] text-gray-400 mt-2 font-mono">{uploadMsg}</p>}
                    </div>

                    {/* Filter / search */}
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="w-3.5 h-3.5 text-gray-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search role / cadre…"
                                data-testid="search-input"
                                className="w-full pl-8 pr-3 py-1.5 text-sm bg-card border border-border/40 text-white placeholder:text-gray-600 focus:outline-none focus:border-white/30" />
                        </div>
                    </div>

                    {/* Canonical JD list */}
                    <div className="border border-border/40 bg-card max-h-[600px] overflow-y-auto" data-testid="canonical-list">
                        <div className="p-3 border-b border-border/40 flex items-center justify-between">
                            <p className="text-xs font-mono uppercase tracking-wider text-gray-500">Canonical JDs ({filteredCJ.length})</p>
                            <button
                                onClick={() => setBulkOpen(true)}
                                disabled={!counts.pending_review}
                                data-testid="bulk-approve-btn"
                                className="text-xs text-emerald-300 hover:text-emerald-200 disabled:text-gray-700 transition-colors">
                                Bulk approve →
                            </button>
                        </div>
                        {filteredCJ.length === 0 ? (
                            <div className="p-6 text-center">
                                <p className="text-sm text-gray-500">No canonical JDs match.</p>
                            </div>
                        ) : (
                            <div>
                                {filteredCJ.map(c => (
                                    <button key={c.id}
                                        onClick={() => setSelectedCJID(c.id)}
                                        data-testid={`cjd-row-${c.id}`}
                                        className={cls(
                                            "w-full text-left p-3 border-b border-border/40 transition-colors",
                                            selectedCJID === c.id ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"
                                        )}>
                                        <div className="flex items-center justify-between mb-1">
                                            <p className="text-sm text-white font-medium truncate">{c.role_title || "(untitled)"}</p>
                                            <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <StatusBadge status={c.approval_status} />
                                            <ConfidencePill value={c.normalization_confidence_score} />
                                            <span className="text-[10px] font-mono text-gray-600 uppercase tracking-wider">{c.cadre_signal || "—"}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Source documents (in-flight queue) */}
                    {sources.length > 0 && (
                        <div className="border border-border/40 bg-card max-h-[300px] overflow-y-auto">
                            <div className="p-3 border-b border-border/40">
                                <p className="text-xs font-mono uppercase tracking-wider text-gray-500">Source Documents ({sources.length})</p>
                            </div>
                            {sources.map(s => (
                                <div key={s.id} className="p-3 border-b border-border/40 flex items-center gap-3" data-testid={`source-row-${s.id}`}>
                                    <FileText className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white truncate">{s.filename}</p>
                                        <p className="text-[10px] font-mono text-gray-600 mt-0.5">
                                            {s.detected_format} • {s.char_count} chars • {s.extraction_confidence}%
                                        </p>
                                    </div>
                                    {s.canonical ? (
                                        <span className="text-[10px] font-mono text-emerald-400 uppercase">linked</span>
                                    ) : (s.extraction_confidence || 0) < 60 ? (
                                        <span className="text-[10px] font-mono text-red-400 uppercase">blocked</span>
                                    ) : (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* RIGHT pane */}
                <div className="col-span-8">
                    {selectedCJID ? (
                        <CanonicalEditor
                            key={selectedCJID}
                            canonicalJDId={selectedCJID}
                            onChanged={() => { refresh(); }}
                        />
                    ) : (
                        <div className="border border-dashed border-border/40 bg-card h-full flex items-center justify-center min-h-[600px]">
                            <div className="text-center">
                                <Eye className="w-8 h-8 text-gray-700 mx-auto mb-3" />
                                <p className="text-sm text-gray-500">Select a canonical JD on the left to review.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <AnimatePresence>
                {bulkOpen && enterpriseId && (
                    <BulkApproveModal
                        enterpriseId={enterpriseId}
                        onClose={() => { setBulkOpen(false); refresh(); }}
                    />
                )}
            </AnimatePresence>

            {/* Phase 2.5.3 — Consistency check + lock-to-cycle */}
            {enterpriseId && <ConsistencyPanel enterpriseId={enterpriseId} />}
        </div>
    );
}

// ---------- Canonical Editor (right pane) ---------------------------------
function CanonicalEditor({ canonicalJDId, onChanged }) {
    const { api } = useAuth();
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showAttribution, setShowAttribution] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [confirmReject, setConfirmReject] = useState(false);

    const load = useCallback(() => {
        api.get(`/v2/admin/jd-normalizer/canonical-jds/detail/${canonicalJDId}`)
            .then(r => { setData(r.data); setDraft(JSON.parse(JSON.stringify(r.data))); })
            .catch(() => {});
    }, [api, canonicalJDId]);

    useEffect(() => { load(); }, [load]);

    const isDirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(data), [draft, data]);

    if (!data || !draft) {
        return (
            <div className="border border-border/40 bg-card h-full flex items-center justify-center min-h-[600px]">
                <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
            </div>
        );
    }

    const blocks = ["identity", "scope", "task_spine", "capability_requirements", "tool_stack", "cultural_values"];
    const provenance = data.source_provenance || {};
    const fieldsModified = provenance.fields_modified_in_review || [];
    const isLocked = data.approval_status === "locked";

    const updateBlock = (block, value) => {
        setDraft(prev => ({ ...prev, [block]: value }));
    };

    const save = async () => {
        if (!isDirty) return;
        setBusy(true);
        try {
            const payload = {};
            blocks.forEach(b => { payload[b] = draft[b]; });
            await api.patch(`/v2/admin/jd-normalizer/canonical-jds/${canonicalJDId}`, payload);
            await load();
            onChanged?.();
        } finally { setBusy(false); }
    };

    const approve = async () => {
        setBusy(true);
        try {
            if (isDirty) {
                const payload = {}; blocks.forEach(b => { payload[b] = draft[b]; });
                await api.patch(`/v2/admin/jd-normalizer/canonical-jds/${canonicalJDId}`, payload);
            }
            await api.post(`/v2/admin/jd-normalizer/canonical-jds/${canonicalJDId}/approve`, { rationale: "approved via review UI" });
            await load();
            onChanged?.();
        } finally { setBusy(false); }
    };

    const reject = async () => {
        if (!rejectReason.trim()) return;
        setBusy(true);
        try {
            await api.post(`/v2/admin/jd-normalizer/canonical-jds/${canonicalJDId}/reject`, { rationale: rejectReason });
            await load();
            onChanged?.();
            setConfirmReject(false);
            setRejectReason("");
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-border/40 bg-card flex flex-col min-h-[600px]" data-testid="canonical-editor">
            {/* Header */}
            <div className="p-4 border-b border-border/40 flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <h2 className="text-lg font-display text-white truncate">{data.identity?.role_title || "(untitled)"}</h2>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span data-testid="status-badge"><StatusBadge status={data.approval_status} /></span>
                        <ConfidencePill value={data.normalization_confidence_score} />
                        <span className="text-[10px] font-mono text-gray-600 uppercase">{data.identity?.cadre_signal || "—"}</span>
                        {fieldsModified.length > 0 && (
                            <span data-testid="modified-count" className="text-[10px] font-mono text-blue-300 px-1.5 py-0.5 border border-blue-500/40 bg-blue-500/10">
                                {fieldsModified.length} edits
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                        <input type="checkbox" checked={showAttribution} onChange={e => setShowAttribution(e.target.checked)}
                            data-testid="attribution-toggle" className="accent-white" />
                        <Sparkles className="w-3 h-3" /> Source attribution
                    </label>
                </div>
            </div>

            {/* Confidence bars */}
            <div className="p-4 border-b border-border/40 grid grid-cols-3 gap-4 text-xs">
                {[
                    { lbl: "Completeness", val: provenance.completeness_pct },
                    { lbl: "Unambiguity", val: provenance.unambiguity_pct },
                    { lbl: "Consistency", val: provenance.consistency_pct },
                ].map(({ lbl, val }) => (
                    <div key={lbl}>
                        <div className="flex items-center justify-between mb-1">
                            <span className="font-mono uppercase text-gray-500">{lbl}</span>
                            <span className="font-mono text-white">{fmtPct(val)}</span>
                        </div>
                        <div className="h-1 bg-white/[0.04]">
                            <div className="h-1 bg-white/60" style={{ width: `${Math.min(100, val || 0)}%` }} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Body — block sections */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
                {blocks.map(block => (
                    <BlockSection
                        key={block}
                        title={block.replace(/_/g, " ")}
                        block={block}
                        value={draft[block]}
                        original={data[block]}
                        onChange={(v) => updateBlock(block, v)}
                        readOnly={isLocked}
                        showAttribution={showAttribution}
                        provenance={provenance}
                    />
                ))}
            </div>

            {/* Footer actions */}
            <div className="p-4 border-t border-border/40 flex items-center justify-between gap-3">
                <div className="text-xs text-gray-500 font-mono">
                    {isDirty ? <span className="text-amber-300">● Unsaved edits</span> : <span>No edits</span>}
                </div>
                <div className="flex items-center gap-2">
                    {!isLocked && data.approval_status !== "approved" && (
                        <>
                            <button onClick={() => setConfirmReject(true)} disabled={busy}
                                data-testid="reject-btn"
                                className="px-3 py-1.5 text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                                Reject
                            </button>
                            <button onClick={save} disabled={!isDirty || busy}
                                data-testid="save-btn"
                                className="px-3 py-1.5 text-xs border border-white/20 text-white hover:bg-white/[0.04] transition-colors disabled:opacity-40">
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save edits"}
                            </button>
                            <button onClick={approve} disabled={busy}
                                data-testid="approve-btn"
                                className="px-3 py-1.5 text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 transition-colors disabled:opacity-40">
                                {isDirty ? "Save & Approve" : "Approve"}
                            </button>
                        </>
                    )}
                    {data.approval_status === "approved" && (
                        <span className="text-xs text-emerald-300 font-mono">✓ Approved</span>
                    )}
                </div>
            </div>

            {/* Reject confirm modal */}
            <AnimatePresence>
                {confirmReject && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
                        <div className="bg-card border border-border/40 max-w-md w-full p-5">
                            <h3 className="text-base font-display text-white mb-1">Reject canonical JD</h3>
                            <p className="text-xs text-gray-500 mb-3">Provide a rationale (required for audit trail).</p>
                            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                                data-testid="reject-rationale"
                                className="w-full h-24 bg-background border border-border/40 text-sm text-white p-2 focus:outline-none focus:border-white/30" />
                            <div className="flex justify-end gap-2 mt-3">
                                <button onClick={() => setConfirmReject(false)} className="px-3 py-1.5 text-xs text-gray-400 hover:text-white">Cancel</button>
                                <button onClick={reject} disabled={!rejectReason.trim() || busy}
                                    data-testid="confirm-reject-btn"
                                    className="px-3 py-1.5 text-xs bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 disabled:opacity-40">
                                    Confirm reject
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ---------- Block Section + recursive JsonEditor live in ./jd_normalizer/JsonEditor.js
// (kept separate so CRA's babel transform doesn't recurse through this file's tree)

// ---------- Bulk Approve modal --------------------------------------------
function BulkApproveModal({ enterpriseId, onClose }) {
    const { api } = useAuth();
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [phase, setPhase] = useState("review"); // review | confirm | done
    const [auditModified, setAuditModified] = useState(0);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get(`/v2/admin/jd-normalizer/bulk-approve/preview/${enterpriseId}`)
            .then(r => setPreview(r.data))
            .catch(() => {});
    }, [api, enterpriseId]);

    const confirm = async () => {
        if (!preview) return;
        setBusy(true);
        try {
            const r = await api.post(`/v2/admin/jd-normalizer/bulk-approve/confirm/${enterpriseId}`, {
                audit_sample_ids: preview.audit_sample_ids,
                auto_approve_ids: preview.auto_approve_ids,
                audit_sample_modified_count: auditModified,
            });
            setResult(r.data);
            setPhase("done");
        } finally { setBusy(false); }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
            <div className="bg-card border border-border/40 max-w-2xl w-full p-5" data-testid="bulk-approve-modal">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-display text-white">Bulk approve canonical JDs</h3>
                        <p className="text-xs text-gray-500 mt-0.5 font-mono">
                            Stratified audit-sample workflow per Schema §7.2/7.3
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none">×</button>
                </div>

                {!preview ? (
                    <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>
                ) : phase === "done" ? (
                    <div data-testid="bulk-result" className="space-y-2">
                        {result?.status === "audit_sample_expanded" ? (
                            <>
                                <p className="text-sm text-amber-300">⚠ Audit sample expanded</p>
                                <p className="text-xs text-gray-400">{result.reason}</p>
                                <p className="text-xs text-gray-500">Expanded sample: {result.expanded_sample_ids?.length} additional JDs</p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-emerald-300">✓ {result?.approved_count || 0} JDs approved</p>
                                <p className="text-xs text-gray-500">Audit sample size: {result?.audit_sample_size || 0}</p>
                            </>
                        )}
                        <div className="flex justify-end pt-3">
                            <button onClick={onClose} className="px-3 py-1.5 text-xs border border-white/20 text-white hover:bg-white/[0.04]">Close</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-3 gap-3 mb-4 text-xs">
                            <div className="p-3 border border-border/40 bg-background/30">
                                <p className="font-mono text-gray-500 uppercase">Threshold</p>
                                <p className="text-xl font-display text-white">{preview.threshold}%</p>
                            </div>
                            <div className="p-3 border border-border/40 bg-background/30">
                                <p className="font-mono text-gray-500 uppercase">Audit sample</p>
                                <p className="text-xl font-display text-white">{preview.audit_sample_ids.length}</p>
                            </div>
                            <div className="p-3 border border-border/40 bg-background/30">
                                <p className="font-mono text-gray-500 uppercase">Auto-approve</p>
                                <p className="text-xl font-display text-white">{preview.auto_approve_ids.length}</p>
                            </div>
                        </div>

                        <div className="space-y-2 mb-4">
                            <p className="text-xs font-mono uppercase text-gray-500">Audit sample (review these, then confirm)</p>
                            <div className="border border-border/40 max-h-48 overflow-y-auto">
                                {preview.audit_sample.map(s => (
                                    <div key={s.id} className="p-2 border-b border-border/40 flex items-center justify-between text-xs" data-testid={`audit-sample-${s.id}`}>
                                        <span className="text-white">{s.role_title}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-gray-500">{s.cadre_signal}</span>
                                            <ConfidencePill value={s.normalization_confidence_score} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="border border-amber-500/30 bg-amber-500/5 p-3 mb-4">
                            <p className="text-xs text-amber-200 mb-2">
                                Did you find issues in any audit-sample JD that required edits?
                            </p>
                            <input type="number" min="0" max={preview.audit_sample.length} value={auditModified}
                                onChange={e => setAuditModified(Number(e.target.value || 0))}
                                data-testid="audit-modified-input"
                                className="bg-background border border-border/40 px-2 py-1 text-sm text-white w-24 font-mono" />
                            <span className="text-[11px] text-gray-500 ml-2">JDs modified during audit</span>
                            <p className="text-[10px] text-gray-500 mt-2">
                                If &gt; 0, the system will expand the sample instead of approving — per Schema §7.3.
                            </p>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-400 hover:text-white">Cancel</button>
                            <button onClick={confirm} disabled={busy}
                                data-testid="confirm-bulk-btn"
                                className="px-3 py-1.5 text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40 inline-flex items-center gap-2">
                                {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                                Confirm bulk approve <ArrowRight className="w-3 h-3" />
                            </button>
                        </div>
                    </>
                )}
            </div>
        </motion.div>
    );
}
